import { createMockExamApi, deleteMockExamApi, updateMockExamApi } from "@/api/services/admin/learning-content";
import { CardContainer } from "@/components/commons/CardContainer";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useMockExam } from "@/features/learning-content/hooks/useMockExam";
import { mockExamEditSchema } from "@/features/learning-content/schema/MockExamEditSchema";
import { TMockExamEditForm } from "@/features/learning-content/types/learningContent";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, CircularProgress, InputAdornment, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

// Mock exam edit page (模擬試験の設定)
export const MockExamEditPage: FC = () => {
  const { id, mockExamId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const deleteDialog = useDialog();

  // Disables the Form's unsaved-changes blocker while deleting, so the confirmed
  // deletion navigation is not interrupted by an extra "leave page?" prompt when the form is dirty.
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: industryData } = useIndustryDetail(id);
  const { data: mockExamData, isLoading: isLoadingMockExam } = useMockExam(id, mockExamId);

  // Create mode when there is no mockExamId in the route (.../mock-exam/create)
  const isCreateMode = !mockExamId;

  const industryName = industryData?.name ?? "";

  // Map existing API data to form values, or show empty form when creating
  const initialValues = useMemo<TMockExamEditForm>(() => {
    if (!mockExamData) {
      return {
        name: "",
        examDuration: null,
        passingScore: null,
        totalScore: null,
        allowReferenceBooks: false,
        allowCalculator: false,
        allowClock: false,
        internalMemo: null,
        spreadsheetUrl: "",
      };
    }
    return {
      name: mockExamData.name ?? "",
      examDuration: mockExamData.examDuration ?? null,
      passingScore: mockExamData.passingScore ?? null,
      totalScore: mockExamData.totalScore ?? null,
      allowReferenceBooks: mockExamData.allowReferenceBooks ?? false,
      allowCalculator: mockExamData.allowCalculator ?? false,
      allowClock: mockExamData.allowClock ?? false,
      internalMemo: mockExamData.internalMemo ?? null,
      spreadsheetUrl: mockExamData.spreadsheetUrl ?? "",
    };
  }, [mockExamData]);

  const methods = useForm<TMockExamEditForm, object, TMockExamEditForm>({
    defaultValues: initialValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<TMockExamEditForm>(mockExamEditSchema as any),
    mode: "all",
    reValidateMode: "onBlur",
  });

  // Reset form when API data loads
  useEffect(() => {
    if (mockExamData) {
      methods.reset(initialValues);
    }
  }, [mockExamData, initialValues, methods]);
  useTriggerValidate({ methods, name: "totalScore", triggerFields: ["passingScore"] });

  const onSubmit = async (data: TMockExamEditForm) => {
    if (!id) return;

    const payload = {
      name: data.name?.trim() ?? "",
      examDuration: data.examDuration ?? null,
      passingScore: data.passingScore ?? null,
      totalScore: data.totalScore ?? null,
      allowReferenceBooks: data.allowReferenceBooks ?? false,
      allowCalculator: data.allowCalculator ?? false,
      allowClock: data.allowClock ?? false,
      internalMemo: data.internalMemo || null,
      spreadsheetUrl: data.spreadsheetUrl || "",
    };

    try {
      // Create a new mock exam, or update the existing one
      let savedMockExamId: string;
      if (isCreateMode) {
        const created = await createMockExamApi(id, payload);
        savedMockExamId = String(created.data.id);
      } else {
        await updateMockExamApi(id, mockExamId!, payload);
        savedMockExamId = mockExamId!;
      }

      toast.success(API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_LIST, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM, id, savedMockExamId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_QUESTION_LIST, id, savedMockExamId] });

      navigate(getDynamicRoute(AppRoutes.learningContentMockExamDetail, { id, mockExamId: savedMockExamId }));
    } catch (error) {
      showError(error);
    }
  };

  const handleBack = () => {
    if (isCreateMode) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id: id! }));
    } else if (id && mockExamId) {
      navigate(getDynamicRoute(AppRoutes.learningContentMockExamDetail, { id, mockExamId }));
    }
  };

  const handleDelete = async () => {
    if (!id || !mockExamId) return;
    const confirmed = await deleteDialog.show();
    if (!confirmed) return;

    try {
      // Set before the awaited API call so React re-renders and unmounts the blocker before navigate.
      setIsDeleting(true);
      const rs = await deleteMockExamApi(id, mockExamId);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_LIST, id] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_QUESTION_LIST, id, mockExamId] }),
      ]);
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } catch (error) {
      setIsDeleting(false); // Re-enable the blocker if deletion failed and we stay on the page.
      showError(error);
    }
  };

  if (isLoadingMockExam) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EditWrapper gap={4}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            {industryName}のコンテンツ管理 - 模擬試験
          </Typography>
        }
        description={
          <Typography variant="body14">
            対象分野の模擬試験に関する設定をしてください。通常、初回設定時および試験内容の変更が生じた場合のみ編集します。
          </Typography>
        }
        enablePageBack={false}
        rightContent={
          !isCreateMode && (
            <Button variant="danger" onClick={handleDelete}>
              模擬試験を削除
            </Button>
          )
        }
      />
      <Form
        methods={methods}
        onSubmit={onSubmit}
        schema={mockExamEditSchema}
        enableBlocker={!methods.formState.isSubmitting && !isDeleting}
        initialValues={initialValues}
      >
        <MenuNavigate
          contents={[
            {
              title: "試験情報",
              content: <ExamInfoSection />,
            },
            {
              title: "模擬試験データ",
              content: <MockExamDataSection />,
            },
          ]}
          menuPrefix={
            <Typography variant="sub16Bold" sx={{ padding: "8px 16px", paddingLeft: "12px" }}>
              模擬試験
            </Typography>
          }
          contentWidth="900px"
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" gap={2} alignItems="center">
                  <Button size="sm-round" variant="secondary" onClick={handleBack}>
                    戻る
                  </Button>
                </Stack>
                <Button disabled={methods.formState.isSubmitting} size="sm-round" type="submit">
                  保存
                </Button>
              </Stack>
            </FooterContainer>
          }
        />
      </Form>

      <ConfirmDeleteModal
        open={deleteDialog.open}
        onClose={deleteDialog.cancel}
        onOk={deleteDialog.confirm}
        message="この模擬試験を削除してもよろしいですか？"
      />
    </EditWrapper>
  );
};

// 試験情報 section - exam info fields
const ExamInfoSection: FC = () => {
  const { control } = useFormContext<TMockExamEditForm>();

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={2}>
        <Typography variant="head20Bold" mb={1}>
          試験情報
        </Typography>
        <Stack gap={2}>
          <FormField control={control} name="name">
            <Input fullWidth placeholder="入力してください" autoComplete="off" />
          </FormField>
        </Stack>
        <Stack direction="row" gap={3}>
          <Stack flex={1}>
            <FormField control={control} name="examDuration">
              <Input fullWidth placeholder="入力してください" endAdornment={<InputAdornment position="end">分</InputAdornment>} />
            </FormField>
          </Stack>
          <Stack flex={1}>
            <FormField control={control} name="passingScore">
              <Input fullWidth placeholder="入力してください" />
            </FormField>
            <Typography variant="cap12" sx={{ color: "text.secondary", mt: 0.5 }}>
              1点以上、満点未満にしてください。
            </Typography>
          </Stack>
          <Stack flex={1}>
            <FormField control={control} name="totalScore">
              <Input fullWidth placeholder="入力してください" />
            </FormField>
            <Typography variant="cap12" sx={{ color: "text.secondary", mt: 0.5 }}>
              模擬試験データの配点合計と一致する必要あり
            </Typography>
          </Stack>
        </Stack>

        {/* 試験時・模擬試験で利用可能な物 - checkboxes matching UI order: 参考書, 電卓・計算機, 時計 */}
        <Box>
          <Typography variant="body14" sx={{ color: "text.primary", fontSize: "12px", mb: 1 }}>
            試験時・模擬試験で利用可能な物
          </Typography>
          <Box sx={{ display: "flex", "& > *": { margin: 0 } }}>
            <FormField control={control} name="allowReferenceBooks" sx={{ margin: 0 }}>
              <Checkbox label="参考書" />
            </FormField>
            <FormField control={control} name="allowCalculator" sx={{ margin: 0 }}>
              <Checkbox label="電卓・計算機" />
            </FormField>
            <FormField control={control} name="allowClock" sx={{ margin: 0 }}>
              <Checkbox label="時計" />
            </FormField>
          </Box>
          <Typography variant="body14" sx={{ color: "text.secondary", fontSize: "12px" }}>
            ※チェックした物は、模擬試験の開始前に利用可能であることが示されます。
          </Typography>
        </Box>

        <FormField control={control} name="internalMemo">
          <Input fullWidth placeholder="入力してください" autoComplete="off" />
        </FormField>
      </Stack>
    </CardContainer>
  );
};

// 模擬試験データ section - spreadsheet URL field
const MockExamDataSection: FC = () => {
  const { control } = useFormContext<TMockExamEditForm>();

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="head20Bold">模擬試験データ</Typography>
        <FormField control={control} name="spreadsheetUrl">
          <Input fullWidth placeholder="入力してください" />
        </FormField>
      </Stack>
    </CardContainer>
  );
};

const EditWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
